import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { GiCastle, GiCyberEye, GiDinosaurRex, GiModernCity, GiPagoda, GiPirateFlag, GiRadioactive, GiRocket, GiSpookyHouse } from '../icons/startupGameIcons';
import Swal from 'sweetalert2';
import { LIGHT_MODE_ENABLED, useLightMode } from '../context/ThemeContext';
import { useNavLabel } from '../utilities/genreIcons';
import { DEFAULT_GENRE, normalizeGenre } from '../utilities/themeColors';
import DeleteAccountDialog from './DeleteAccountDialog';
import SrdSpellImport from './SrdSpellImport';
import { useFeatureFlags } from '../context/FeatureFlagContext';

const DARK = 'var(--lm-bg-1a1f2b)';
const BORDER = 'rgba(var(--th-6c4f9b-rgb), 0.4)';

const GAME_SYSTEMS = [
    { key: 'DND_2014',        label: '5th Edition (2014)', color: '#e53935' },
    { key: 'DND_2024',        label: '5th Edition (2024)', color: '#FFD700' },
    { key: 'PATHFINDER_1E',   label: 'Pathfinder First Edition',  color: '#7FA650' },
    { key: 'PATHFINDER_2E',   label: 'Pathfinder Second Edition', color: '#C8960C' },
    { key: 'STARFINDER',      label: 'Starfinder First Edition',  color: '#00D4FF' },
    { key: 'STARFINDER_2E',   label: 'Starfinder Second Edition', color: '#8C7CFF' },
    // { key: 'CALL_OF_CTHULHU', label: 'Call of Cthulhu',  color: '#5EAD84' }, // hidden — see HIDDEN_SYSTEM_KEYS in GameMasterScreen.js
    { key: 'DND_35E',         label: '3.5 SRD',          color: '#B87333' },
    { key: 'BLADES_IN_THE_DARK', label: 'Blades in the Dark SRD', color: '#8FB8C9' },
    // { key: 'DAGGERHEART',     label: 'Daggerheart Compatible', color: '#4ECDC4' }, // hidden — see HIDDEN_SYSTEM_KEYS in GameMasterScreen.js
];

export default function GlobalSettingsModal({ show, onHide }) {
    const userJSON = JSON.parse(sessionStorage.getItem('user'));
    const { setLightMode, setShowTooltips, setTavernPanZoomEnabled, setTavernOneClickNav, setShowAiArt, setGenre } = useLightMode();
    const tavernLabel = useNavLabel('tavern');
    const { superAdmin } = useFeatureFlags();
    const [loading, setLoading] = useState(false);
    const [cachedSettings, setCachedSettings] = useState(null);
    const [localLightMode, setLocalLightMode] = useState(false);
    const [localShowTooltips, setLocalShowTooltips] = useState(true);
    const [localTavernPanZoom, setLocalTavernPanZoom] = useState(true);
    const [localTavernOneClick, setLocalTavernOneClick] = useState(false);
    const [localShowAiArt, setLocalShowAiArt] = useState(true);
    const [localGenre, setLocalGenre] = useState(DEFAULT_GENRE);
    // null = all enabled; array = explicit list
    const [enabledSystems, setEnabledSystems] = useState(null);
    // Its own modal rather than a section of this one, so it opens after this one closes.
    const [showDeleteAccount, setShowDeleteAccount] = useState(false);

    useEffect(() => {
        if (!show) return;
        const headers = { 'Authorization': 'Bearer ' + userJSON.accessToken };
        fetch(`${process.env.REACT_APP_API_PROXY}/settings/getSettingsForUser`, { headers })
            .then(r => r.json())
            .then(data => {
                setCachedSettings(data);
                setLocalLightMode(!!data.lightMode);
                setLocalShowTooltips(data.showTooltips !== false);
                setLocalTavernPanZoom(data.tavernPanZoomEnabled !== false);
                setLocalTavernOneClick(data.tavernOneClickNav === true);
                setLocalShowAiArt(data.showAiArt !== false);
                setLocalGenre(normalizeGenre(data.uiGenre));
                if (data.enabledGameSystems) {
                    try {
                        const parsed = typeof data.enabledGameSystems === 'string'
                            ? JSON.parse(data.enabledGameSystems)
                            : data.enabledGameSystems;
                        setEnabledSystems(Array.isArray(parsed) ? parsed : null);
                    } catch (_) {
                        setEnabledSystems(null);
                    }
                } else {
                    setEnabledSystems(null);
                }
            })
            .catch(() => setEnabledSystems(null));
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    const isEnabled = (key) => enabledSystems === null || enabledSystems.includes(key);

    const toggleSystem = (key) => {
        if (enabledSystems === null) {
            setEnabledSystems(GAME_SYSTEMS.map(s => s.key).filter(k => k !== key));
        } else {
            const next = isEnabled(key)
                ? enabledSystems.filter(k => k !== key)
                : [...enabledSystems, key];
            setEnabledSystems(next.length === GAME_SYSTEMS.length ? null : next);
        }
    };

    function handleSave() {
        if (!cachedSettings) return;
        setLoading(true);
        const headers = {
            'Authorization': 'Bearer ' + userJSON.accessToken,
            'Content-Type': 'application/json',
        };
        const payload = {
            ...cachedSettings,
            lightMode: localLightMode,
            showTooltips: localShowTooltips,
            tavernPanZoomEnabled: localTavernPanZoom,
            tavernOneClickNav: localTavernOneClick,
            showAiArt: localShowAiArt,
            uiGenre: localGenre,
            enabledGameSystems: enabledSystems === null ? null : JSON.stringify(enabledSystems),
            // Left out (undefined is dropped from the JSON, which the server reads as "not
            // changed"): the tabletop's dice tray owns it, and this copy may predate a toggle there.
            monsterRollsPublic: undefined,
        };
        fetch(`${process.env.REACT_APP_API_PROXY}/settings/addUpdateSettings`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
        })
            .then(response => {
                if (!response.ok) throw new Error('Request failed with status ' + response.status);
                setLightMode(localLightMode);
                setShowTooltips(localShowTooltips);
                setTavernPanZoomEnabled(localTavernPanZoom);
                setTavernOneClickNav(localTavernOneClick);
                setShowAiArt(localShowAiArt);
                setGenre(localGenre);
                const cacheKey = 'enabledGameSystems_' + userJSON.username;
                const cacheVal = enabledSystems === null ? null : JSON.stringify(enabledSystems);
                if (cacheVal === null) localStorage.removeItem(cacheKey);
                else localStorage.setItem(cacheKey, cacheVal);
                window.dispatchEvent(new CustomEvent('globalSettingsChanged'));
                Swal.fire({ timer: 2000, title: 'Saved', text: 'Settings updated', icon: 'success', showConfirmButton: false, position: 'bottom-end' });
                onHide();
            })
            .catch(() => {
                Swal.fire({ timer: 3000, title: 'Error', text: 'Could not save settings. Please try again.', icon: 'error', position: 'bottom-end' });
            })
            .finally(() => setLoading(false));
    }

    const sectionLabel = (text) => (
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', color: 'var(--lm-tx-th-9c6fd6)', letterSpacing: '0.08em', mb: 0.75 }}>
            {text}
        </Typography>
    );

    const noneEnabled = enabledSystems !== null && enabledSystems.length === 0;

    // dialogClassName: wider than Bootstrap's default (App.css) so the nine themes fit on one row.
    return (
        <>
        <DeleteAccountDialog show={showDeleteAccount} onHide={() => setShowDeleteAccount(false)}/>
        <Modal show={show} onHide={onHide} centered scrollable data-bs-theme="dark" dialogClassName="global-settings-dialog">
            {/* Header — one row rather than stacked. The header is fixed while the body
                scrolls, so every pixel here is permanently unavailable to the content. */}
            <Modal.Header closeButton style={{
                backgroundColor: DARK,
                borderBottom: `1px solid ${BORDER}`,
                background: `radial-gradient(ellipse at 50% 0%, rgba(var(--th-6c4f9b-rgb), 0.35) 0%, ${DARK} 80%)`,
                alignItems: 'center',
                paddingTop: 12, paddingBottom: 12,
            }}>
                <Settings style={{fontSize: 26, color: 'var(--lm-tx-th-c9a0f0)', filter: 'drop-shadow(0 0 6px rgba(var(--th-c9a0f0-rgb), 0.5))', marginRight: 10}}/>
                <Modal.Title style={{color: 'var(--lm-tx-th-c9a0f0)', fontWeight: 700, letterSpacing: '0.02em', fontSize: '1.05rem'}}>
                    Global Settings
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ backgroundColor: DARK, color: 'var(--lm-tx-f5f5f5)', padding: '20px 24px' }}>

                {/* Display */}
                {sectionLabel('Display')}
                <Divider sx={{ borderColor: BORDER, mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                    {LIGHT_MODE_ENABLED && (
                        <Form.Check
                            type="switch"
                            label="Light Mode"
                            checked={localLightMode}
                            onChange={e => setLocalLightMode(e.target.checked)}
                        />
                    )}
                    <Form.Check
                        type="switch"
                        label="Show Tooltips"
                        checked={localShowTooltips}
                        onChange={e => setLocalShowTooltips(e.target.checked)}
                    />
                </Box>

                {/* Theme: the app's palette and icon set. Applies on Save, like Light Mode. */}
                {sectionLabel('Theme')}
                <Divider sx={{ borderColor: BORDER, mb: 1 }} />
                <Box sx={{ mb: 2 }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={localGenre}
                        onChange={(_, value) => { if (value) setLocalGenre(value); }}
                        aria-label="Theme"
                        sx={{
                            // One row at the modal's width where they fit; on a narrow screen they
                            // wrap to a second centred row rather than overflowing.
                            display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
                            width: 'fit-content', maxWidth: '100%', mx: 'auto',
                            '& .MuiToggleButton-root': {
                                color: 'var(--lm-tx-b8b8c8)', borderColor: BORDER, textTransform: 'none',
                                fontSize: '0.8rem', gap: 0.6, px: 1, whiteSpace: 'nowrap',
                            },
                            '& .MuiToggleButton-root.Mui-selected': {
                                color: 'var(--lm-tx-f5f5f5)', backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.45)',
                            },
                            '& .MuiToggleButton-root.Mui-selected:hover': {
                                backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.6)',
                            },
                        }}
                    >
                        <ToggleButton value="fantasy" aria-label="Fantasy">
                            <GiCastle style={{ fontSize: 16, color: '#C9A0F0' }} /> Fantasy
                        </ToggleButton>
                        <ToggleButton value="scifi" aria-label="Sci-Fi">
                            <GiRocket style={{ fontSize: 16, color: '#9CDFF4' }} /> Sci-Fi
                        </ToggleButton>
                        <ToggleButton value="horror" aria-label="Horror">
                            <GiSpookyHouse style={{ fontSize: 16, color: '#D2404A' }} /> Horror
                        </ToggleButton>
                        <ToggleButton value="postapoc" aria-label="Post-Apocalyptic">
                            <GiRadioactive style={{ fontSize: 16, color: '#BDD369' }} /> Post-Apoc
                        </ToggleButton>
                        <ToggleButton value="pirate" aria-label="Pirate">
                            <GiPirateFlag style={{ fontSize: 16, color: 'var(--lm-tx-e8c15d)' }} /> Pirate
                        </ToggleButton>
                        <ToggleButton value="urban" aria-label="Urban Fantasy">
                            <GiModernCity style={{ fontSize: 16, color: '#F367A8' }} /> Urban
                        </ToggleButton>
                        <ToggleButton value="japan" aria-label="Samurai">
                            <GiPagoda style={{ fontSize: 16, color: '#E06A55' }} /> Samurai
                        </ToggleButton>
                        <ToggleButton value="cyberpunk" aria-label="Cyberpunk">
                            <GiCyberEye style={{ fontSize: 16, color: 'var(--lm-tx-ffe312)' }} /> Cyberpunk
                        </ToggleButton>
                        <ToggleButton value="primal" aria-label="Primal">
                            <GiDinosaurRex style={{ fontSize: 16, color: 'var(--lm-tx-e6ad62)' }} /> Primal
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--lm-tx-888888)', mt: 0.75 }}>
                        Changes the app's colors and icons. Your maps, tokens and characters are not affected.
                    </Typography>
                </Box>

                {/* Creature Art: all the bundled token and bestiary art is AI-generated. */}
                {sectionLabel('Creature Art')}
                <Divider sx={{ borderColor: BORDER, mb: 1 }} />
                <Box sx={{ mb: 2 }}>
                    <Box sx={{
                        border: '1px solid rgba(255, 193, 7, 0.45)', backgroundColor: 'rgba(255, 193, 7, 0.08)',
                        borderRadius: 1, px: 1.5, py: 1, mb: 1,
                    }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--lm-tx-f5f5f5)' }}>
                            <strong>AI-generated art.</strong> The default creature tokens and bestiary pictures
                            included with DropWyrm were created with AI image tools.
                        </Typography>
                    </Box>
                    <Form.Check
                        type="switch"
                        label="Show AI-Generated Creature Art"
                        checked={localShowAiArt}
                        onChange={e => setLocalShowAiArt(e.target.checked)}
                    />
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--lm-tx-888888)' }}>
                        When disabled, creatures without art of your own show a placeholder instead. Art you upload yourself is not affected.
                    </Typography>
                </Box>

                {/* Tavern */}
                {sectionLabel(tavernLabel)}
                <Divider sx={{ borderColor: BORDER, mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                    <Form.Check
                        type="switch"
                        label="One-Click Hotspot Navigation"
                        checked={localTavernOneClick}
                        onChange={e => setLocalTavernOneClick(e.target.checked)}
                    />
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--lm-tx-888888)', mb: 0.75 }}>
                        When enabled, clicking a hotspot in the 3D view goes straight to its page, like the 2D view.
                    </Typography>
                    {/* Moot with one-click on: there are no details to zoom in to. */}
                    <Form.Check
                        type="switch"
                        label="Pan & Zoom on Hotspot Click"
                        checked={localTavernPanZoom && !localTavernOneClick}
                        disabled={localTavernOneClick}
                        onChange={e => setLocalTavernPanZoom(e.target.checked)}
                    />
                    <Typography sx={{ fontSize: '0.65rem', color: 'var(--lm-tx-888888)' }}>
                        When disabled, clicking a tavern hotspot opens its details without moving the camera.
                    </Typography>
                </Box>

                {/* Game Master — Visible Systems */}
                {sectionLabel('Game Master - Visible Systems')}
                <Divider sx={{ borderColor: BORDER, mb: 1.5 }} />
                {/* Two columns, filled row by row, so each game's editions sit side by side. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2, rowGap: 0.25 }}>
                    {GAME_SYSTEMS.map(sys => (
                        <FormControlLabel
                            key={sys.key}
                            control={
                                <Checkbox
                                    checked={isEnabled(sys.key)}
                                    onChange={() => toggleSystem(sys.key)}
                                    size="small"
                                    sx={{ color: sys.color, '&.Mui-checked': { color: sys.color }, py: 0.4 }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: '0.85rem', color: 'var(--lm-tx-f5f5f5)' }}>
                                    {sys.label}
                                </Typography>
                            }
                        />
                    ))}
                </Box>
                {noneEnabled && (
                    <Typography sx={{ fontSize: '0.72rem', color: 'var(--lm-tx-e53935)', mt: 1 }}>
                        At least one system must remain enabled.
                    </Typography>
                )}

                {/* Administration - super admins only (the server enforces it too). */}
                {superAdmin && (
                    <Box sx={{ mt: 2.5 }}>
                        {sectionLabel('Administration')}
                        <Divider sx={{ borderColor: BORDER, mb: 1.5 }} />
                        <SrdSpellImport />
                    </Box>
                )}

                {/* Account */}
                <Box sx={{ mt: 2.5 }}>
                    {sectionLabel('Account')}
                    <Divider sx={{ borderColor: BORDER, mb: 1.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--lm-tx-888888)', flex: '1 1 220px' }}>
                            Permanently delete your account and personal data. You'll have 30 days to change your mind.
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => { onHide(); setShowDeleteAccount(true); }}
                            sx={{
                                color: 'var(--lm-tx-e53935)', borderColor: 'rgba(229, 57, 53, 0.6)', textTransform: 'none',
                                '&:hover': { borderColor: '#e53935', backgroundColor: 'rgba(229, 57, 53, 0.08)' },
                            }}
                        >
                            Delete account…
                        </Button>
                    </Box>
                </Box>
            </Modal.Body>

            <Modal.Footer style={{ backgroundColor: DARK, borderTop: `1px solid ${BORDER}`, padding: '12px 24px' }}>
                <Button onClick={onHide} disabled={loading} sx={{color: 'var(--lm-tx-9e9e9e)', '&:hover': {color: 'var(--lm-tx-f5f5f5)'}}}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || noneEnabled}
                    startIcon={loading ? <CircularProgress size={14} sx={{color: 'var(--lm-tx-f5f5f5)'}}/> : <Settings/>}
                    sx={{
                        ml: 1, backgroundColor: 'var(--th-6c4f9b)',
                        '&:hover': {backgroundColor: 'var(--th-7d5cb3)'},
                        '&:disabled': {backgroundColor: 'rgba(var(--th-6c4f9b-rgb), 0.3)', color: 'rgba(245,245,245,0.3)'},
                    }}
                >
                    {loading ? 'Saving…' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
}
